import { useEffect, useLayoutEffect, useRef, useState } from "react"
import UrlForm, { fieldClasses } from "./UrlForm"
import Listbox from "./Listbox"
import CustomizePanel from "./CustomizePanel"
import LivePreview from "./LivePreview"
import { BLEED_DASHBOARD, FINISH_DRAWER, NARROW_QUERY, PINNED_PREVIEW, RAIL_REST } from "../lib/previewLayout"
import useMediaQuery from "../hooks/useMediaQuery"
import EditDestination from "./EditDestination"
import SaveStatus from "./SaveStatus"

const CREATE_FORM_ID = "dashboard-create"

// The finish bar sticks to the bottom of the screen, so "Create my code" stays
// in reach while the person styles the code. On a phone the destination field
// can sit right where the bar sticks, under it (#334). So the bar sticks only
// while the field is clear of it: above the bar's line, or scrolled past.
// Until then the bar stays in its place below the design options.
// `gap` is the bar's distance from the bottom of the screen when it sticks:
// none for the phone drawer, 12px (bottom-3) for the desktop card. It also
// re-runs the effect when the layout switches, which swaps both elements.
function useClearOf(fieldRef, barRef, gap) {
  const [clear, setClear] = useState(true)
  useLayoutEffect(() => {
    const field = fieldRef.current
    const bar = barRef.current
    if (!field || !bar) return
    let frame = 0
    const check = () => {
      frame = 0
      const line = window.innerHeight - bar.offsetHeight - gap
      setClear(field.getBoundingClientRect().bottom <= line)
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(check) }
    check()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    // Content above the field (the stat cards, a notice) can load and move
    // it with no scroll, and an error under the field makes it taller.
    const resizes = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule)
    resizes?.observe(document.body)
    resizes?.observe(field)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      resizes?.disconnect()
    }
  }, [fieldRef, barRef, gap])
  return clear
}

// The redesigned in-dashboard QR creator (QrBuilder layout="dashboard").
//
// A two-pane workspace that does NOT reshuffle between the pre- and post-create
// states: destination + a collapsible, everything-visible customise grid on the
// left; a persistent, sticky live preview carrying the short link + downloads on
// the right. The right rail is one element across both states — before a code
// exists it shows the styled preview + a caption + disabled downloads; after
// create it gains the short link and enables the downloads. This replaces the
// old "LivePreview pre-submit -> ResultCard post-submit" swap.
//
// The finish (#316): one primary button, in a bar that sticks to the bottom of
// the screen under the design options. "Create my code" before the code
// exists; after, the save status and "Done", which hands the code to "Your
// codes" and clears the builder. The heading says "Code created", and "New
// code" is a quiet link in the header, away from where the create button was,
// so a second click can't start over by accident.
//
// All state, the debounced live-restyle effect, editingCode seeding, and bot
// protection stay in QrBuilder; this component is presentation + local UI bits
// (copy feedback, the destination-edit toggle).

function pngFor(imageUrl) {
  if (!imageUrl) return null
  return imageUrl.replace(/\.svg(\?.*)?$/, ".png$1")
}

const finishBtnBase =
  "flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-voltage text-coal font-semibold shadow-voltage transition " +
  "hover:bg-voltage-dark disabled:opacity-60 disabled:cursor-not-allowed " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
const finishBtn = finishBtnBase + " min-h-[48px] px-7"
// Compact, in the phone drawer (lib/previewLayout).
const finishBtnNarrow = finishBtnBase + " min-h-[44px] px-4 text-sm"

const FINISH_CARD =
  "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--color-border)] dark:border-storm " +
  "bg-white dark:bg-midnight px-4 py-3 shadow-md"

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

// "Start from the look of" (#343). Six codes alike used to mean styling six
// times; now a new code starts with the newest code's look and the person
// changes what differs. It copies once, at create: Create many's "Look" with
// one row. Not "saved styles", which TangoQR doesn't have. The labels match
// Create many's picker; the control matches the Destination picker above it.
function LookPicker({ look, disabled }) {
  const options = [
    { id: "", label: "Plain black and white" },
    ...look.codes.map((c) => ({ id: c.token, label: `Match ${c.display_label || c.token}` }))
  ]
  const current = options.find((o) => o.id === look.token) || options[0]
  return (
    <div>
      <span className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">
        Look
      </span>
      <Listbox
        fill
        options={options}
        value={look.token}
        onChange={(o) => look.onChange(o.id)}
        disabled={disabled}
        label="Look"
        className={fieldClasses(false)}
        buttonProps={{
          "aria-label": `Look: ${current.label}`,
          "aria-describedby": "dashboard-look-hint"
        }}
      />
      <p id="dashboard-look-hint" className="mt-2 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
        Start from a code you already styled. Change anything below.
      </p>
    </div>
  )
}

export default function DashboardCreator({
  status,
  result,
  formError,
  design,
  setDesign,
  restyleError,
  restyleStatus = "idle",
  doneRequested = false,
  onSubmit,
  onReset,
  onDone,
  onUpdated,
  botProtectionFields = null,
  isPaid = false,
  onUpgrade,
  isEditing = false,
  look = null,
  // Making a hosted contact page (#332), per user.
  contactPages = false
}) {
  const isLoading = status === "loading"
  const created = status === "success" && !!result
  // The code a new one starts from (#343), before it exists.
  const lookCode = !created && look?.token ? look.codes.find((c) => c.token === look.token) : null
  const [copied, setCopied] = useState(false)
  const [editingDest, setEditingDest] = useState(false)
  const destinationRef = useRef(null)
  const finishBarRef = useRef(null)
  // Below 900px: one column, the pinned preview and the drawer
  // (lib/previewLayout). Switched in JS, not CSS: the preview must pin inside a
  // box with the form, and on desktop the two are in different columns.
  const narrow = useMediaQuery(NARROW_QUERY)
  const finishBarSticks = useClearOf(destinationRef, finishBarRef, narrow ? 0 : 12)

  // Reset the local destination-edit toggle whenever we switch codes / reset.
  useEffect(() => { setEditingDest(false) }, [result?.token])

  // The create button is in the finish bar, which can be a long way below the
  // destination field. A refused create puts its message on that field, so
  // take the person there. Keyed on the message: an integrity refusal arrives
  // as a new object on every render, and focus must not jump on each one.
  const formErrorKey = formError?.message ?? formError
  useEffect(() => {
    if (formErrorKey) document.getElementById("destination-url")?.focus()
  }, [formErrorKey])

  const shareUrl = result?.redirect_url || result?.short_url
  const shortDisplay = shareUrl ? shareUrl.replace(/^https?:\/\//, "") : ""
  const editable = result?.editable !== false

  async function handleCopy() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { setCopied(false) }
  }

  // Preview encodes the real short URL once a code exists, so the module
  // pattern matches the printed code; pre-create it falls back to the generic
  // preview payload (LivePreview omits the token). Starting from another
  // code's look, the preview borrows that code's saved logo, never its link.
  const previewDesign = created
    ? { ...design, token: result.token }
    : lookCode ? { ...design, referenceToken: lookCode.token } : design

  // ── Left: destination block ────────────────────────────────────────────
  const destinationBlock = created ? (
    <div>
      <span className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">
        Destination
        <span className="ml-2 font-normal text-xs text-[color:var(--color-muted)] dark:text-muted-light">— changes apply live</span>
      </span>
      {editingDest && editable ? (
        <EditDestination
          code={result}
          contactPages={contactPages}
          onSave={(updated) => { setEditingDest(false); onUpdated?.(updated) }}
          onCancel={() => setEditingDest(false)}
        />
      ) : (
        <div className="flex items-center gap-2 min-w-0">
          {result.contact_page ? (
            // A hosted contact page (#332): its address is ours, so the person
            // gets a way to open it rather than a random id to read.
            <span className="text-sm text-ink dark:text-cloud flex-1 min-w-0 px-3 py-2.5 rounded border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal flex items-center justify-between gap-2">
              <span className="truncate">Your contact page</span>
              <a href={result.destination_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 font-semibold text-voltage-deep dark:text-voltage-light underline underline-offset-2 hover:no-underline">
                Open your page
              </a>
            </span>
          ) : (
            <span className="font-mono text-sm text-ink dark:text-cloud truncate flex-1 min-w-0 px-3 py-2.5 rounded border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal" title={result.destination_url}>
              {result.destination_url}
            </span>
          )}
          {editable ? (
            <button
              type="button"
              onClick={() => setEditingDest(true)}
              aria-label="Edit destination"
              title="Edit destination"
              className="flex-shrink-0 inline-flex items-center justify-center min-h-[44px] w-11 rounded border border-[color:var(--color-border)] dark:border-storm text-voltage hover:border-voltage transition"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M3 17.25V21h3.75l11-11-3.75-3.75-11 11zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => (onUpgrade ? onUpgrade("locked", { destination: result.destination_url }) : window.location.assign("/pricing"))}
              aria-label="Editing this code requires Pro"
              title="Editing locked — upgrade to Pro"
              className="flex-shrink-0 inline-flex items-center justify-center min-h-[44px] w-11 rounded border border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M18 8h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-7-2a3 3 0 0 1 6 0v2h-6V6zm1 11a2 2 0 1 1 2-2 2 2 0 0 1-2 2z"/></svg>
            </button>
          )}
        </div>
      )}
    </div>
  ) : (
    <>
      <UrlForm layout="dashboard" formId={CREATE_FORM_ID} hideSubmit onSubmit={onSubmit} disabled={isLoading} serverError={formError} contactPages={contactPages} />
      {botProtectionFields}
    </>
  )

  // ── The preview's link and downloads ──────────────────────────────────
  const previewActions = (
    <>
      {created ? (
        <div className="w-full flex items-center gap-2 px-3 py-2 rounded border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">Link</span>
          <span className="font-mono text-sm text-ink dark:text-cloud truncate flex-1 min-w-0">{shortDisplay}</span>
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy short link"
            className={"flex-shrink-0 transition " + (copied ? "text-voltage-dark dark:text-voltage-light" : "text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud")}
          >
            {copied
              ? <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              : <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
          </button>
        </div>
      ) : (
        <p className="text-xs text-center text-[color:var(--color-muted)] dark:text-muted-light w-full">
          Create the code to get its short link and downloads.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 w-full">
        {created ? (
          <>
            <a href={pngFor(result.image_url)} download={`tango-qr-${result.token}.png`} className="inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded border border-voltage text-voltage text-sm font-semibold hover:bg-voltage-pale dark:hover:bg-voltage/15 transition">
              <DownloadIcon /> PNG
            </a>
            <a href={result.image_url} download={`tango-qr-${result.token}.svg`} className="inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded bg-voltage text-coal text-sm font-semibold hover:bg-voltage-dark transition">
              <DownloadIcon /> SVG
            </a>
          </>
        ) : (
          <>
            <button type="button" disabled className="inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded border border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light text-sm font-semibold opacity-60 cursor-not-allowed"><DownloadIcon /> PNG</button>
            <button type="button" disabled className="inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded border border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light text-sm font-semibold opacity-60 cursor-not-allowed"><DownloadIcon /> SVG</button>
          </>
        )}
      </div>
    </>
  )

  // Free tier gets an action, not a statement of fact — "free codes carry a
  // wordmark" tells the user something without offering to do anything about
  // it. Mirrors HeroResult's rail.
  const planNote = isPaid ? (
    <p className="text-xs text-center text-[color:var(--color-muted)] dark:text-muted-light">
      Pro — no wordmark, every code editable.
    </p>
  ) : (
    <button
      type="button"
      onClick={() => (onUpgrade ? onUpgrade("wordmark") : window.location.assign("/pricing"))}
      className="w-full text-center text-xs font-semibold text-plasma dark:text-plasma-light hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
    >
      Upgrade to remove the Tango wordmark
    </button>
  )

  const header = (
    <header className="flex items-start justify-between gap-3">
      <div>
        <h1 className="font-display font-extrabold tracking-tight text-2xl text-coal dark:text-cloud flex items-center gap-2.5">
          {created && !isEditing && (
            <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-voltage text-coal flex-shrink-0">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </span>
          )}
          {isEditing ? "Edit your code" : created ? "Code created" : "Create a code"}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
          {created
            ? "It's in Your codes. Style it if you like, then press Done."
            : "Paste a link. Style it if you like. Then create it."}
        </p>
      </div>
      {created && (
        <button
          type="button"
          onClick={onReset}
          className="flex-shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-1 text-sm font-semibold text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New code
        </button>
      )}
    </header>
  )

  const form = (
    <>
      <div ref={destinationRef}>{destinationBlock}</div>

      {!created && look?.codes?.length > 0 && (
        <LookPicker look={look} disabled={isLoading} />
      )}

      <div className="min-w-0">
        <CustomizePanel
          layout="dashboard"
          design={design}
          setDesign={setDesign}
          error={restyleError}
          existingLogoUrl={created ? (result?.logo_url || null) : (lookCode?.logo_url || null)}
          canUseShapes={isPaid}
          onUpgrade={onUpgrade}
        />
      </div>
    </>
  )

  // The finish bar (#316). Before, the only button sat above the design
  // options and scrolled away while the person styled. It sticks only while
  // the destination field is clear of it (useClearOf, #334). On a phone it is
  // a drawer on the bottom edge (lib/previewLayout).
  const noteClass = (narrow ? "text-xs leading-snug" : "text-sm") + " text-[color:var(--color-muted)] dark:text-muted-light"
  const finishBar = (
    <div
      ref={finishBarRef}
      className={
        narrow
          ? (finishBarSticks ? "sticky bottom-0 z-30 " : "") + BLEED_DASHBOARD + " " + FINISH_DRAWER
          : (finishBarSticks ? "sticky bottom-3 z-10 " : "") + FINISH_CARD
      }
    >
      {created ? (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 min-w-0">
          <SaveStatus status={restyleStatus} />
          <p className={noteClass}>Changes save as you go.</p>
        </div>
      ) : (
        <p className={"min-w-0 " + noteClass}>You can restyle it after. The link inside the code never changes.</p>
      )}
      {/* Two buttons, two keys. Without them React keeps one <button> and
          changes its type: Done's click clears the builder, the same
          element turns into the submit for the empty form before the click
          finishes, and the browser submits it, so the fresh builder opened
          on "We need a URL" (#343). */}
      {created ? (
        <button
          key="done"
          type="button"
          onClick={onDone}
          disabled={doneRequested}
          className={narrow ? finishBtnNarrow : finishBtn}
        >
          {doneRequested ? "Saving…" : (
            <>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              Done
            </>
          )}
        </button>
      ) : (
        <button key="create" type="submit" form={CREATE_FORM_ID} disabled={isLoading} className={narrow ? finishBtnNarrow : finishBtn}>
          {isLoading ? "Creating…" : (
            <>
              Create my code
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </>
          )}
        </button>
      )}
    </div>
  )

  // ── Layout ──────────────────────────────────────────────────────────────
  // Below 900px, one column: the heading; the preview pinned to the top inside
  // a box with the form, so it stops where the form stops; the drawer; then
  // the link and downloads.
  if (narrow) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <div className="flex flex-col gap-6">
          <div className={BLEED_DASHBOARD + " " + PINNED_PREVIEW}>
            <LivePreview design={previewDesign} maxWidth="max-w-none" pinned />
          </div>
          {form}
        </div>
        {finishBar}
        <div className={RAIL_REST + " flex flex-col items-center gap-3.5"}>{previewActions}</div>
        {planNote}
      </div>
    )
  }

  // Two columns: the left column is a single grid item — a flex column with
  // fixed internal spacing — so collapsing the customise panel can never
  // stretch the gaps between title / destination / customise (the rail's
  // height only sizes its own column, not the left rows). The right rail is
  // one sticky card across both states, top-aligned with the title: before a
  // code exists it shows the styled preview, a caption and disabled
  // downloads; after create it gains the short link and enables the
  // downloads. The code fills the card's width.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_300px] min-[1100px]:grid-cols-[minmax(0,1fr)_340px] gap-x-7 items-start">
      <div className="flex flex-col gap-6 min-w-0">
        {header}
        {form}
        {finishBar}
      </div>
      <aside className="sticky top-[88px] self-start flex flex-col gap-3.5">
        <div className="rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-cloud dark:bg-coal p-4 flex flex-col items-center gap-3.5">
          <LivePreview design={previewDesign} maxWidth="max-w-none" />
          {previewActions}
        </div>
        {planNote}
      </aside>
    </div>
  )
}
